import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/data-access';
import { parseSearch } from '@jane/shared/util';

interface SearchContext<T> {
  resetSearchState: (nextSearchState?: Partial<JaneSearchState<T>>) => void;
  searchState: JaneSearchState<T>;
  setSearchState: (searchState: Partial<JaneSearchState<T>>) => void;
}

const buildSearchState = <T,>(
  initialSearchParams: JaneSearchState<T> = {}
): JaneSearchState<T> => ({
  bucketFilters: {},
  filters: {},
  page: 0,
  rangeFilters: {},
  searchText: '',
  ...initialSearchParams,
});

const SearchContext =
  createContext<SearchContext<MaterializedMenuProduct> | null>(null);
SearchContext.displayName = 'Search';

export const useSearchContext = () => useContextEnsuringProvider(SearchContext);

/**
 * A simplified version of the SearchProvider in \@jane/search.
 *   It does not reference instantsearch, the old Algolia UI dependency.
 *   It does not sync search state with query params.
 */
export const SearchProvider = <T extends object = AlgoliaProduct>({
  children,
}: PropsWithChildren) => {
  const location = useLocation();
  const [searchState, setSearchState] = useState<JaneSearchState<T>>(
    buildSearchState(parseSearch(location.search))
  );

  const setState = useCallback(
    (nextSearchState: Partial<JaneSearchState<T>>) => {
      setSearchState((prevSearchState) => {
        return { ...prevSearchState, ...nextSearchState };
      });
    },
    []
  );

  const resetSearchState = useCallback(
    (nextSearchState: Partial<JaneSearchState<T>> = buildSearchState({})) => {
      setSearchState(() => {
        return nextSearchState;
      });
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      resetSearchState,
      searchState,
      setSearchState: setState,
    }),
    [resetSearchState, searchState, setState]
  );

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};
